import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { FleetGrid } from '../../../../shared/sso-workspace/fleet-grid'
import { FleetPage, Notice } from '../../../../shared/sso-workspace/fleet-page'
import { Pill } from '../../../../shared/sso-workspace/pill'
import { formatAgo, formatDateTime, serverMessage } from '../../../../shared/sso-workspace/format'
import { TableAgInfinite } from '../../../../shared/table-builder/table-ag-infinite'
import { ROW_HEIGHT_TWO_LINE } from '../../../../shared/table-builder/table-ag'
import { ColumnFormat } from '../../../../shared/table-builder/table-columns'
import { IconList } from '../../../../shared/icon/icon'
import { TimeRangeFilter } from '../../../../shared/date/time-range-filter'
import { RANGES } from '../../../../shared/audit/filters-model'
import { listRoutes, listRuns, listTargets } from './alert-routing-api'
import { useCatalog } from './catalog'
import { ALERT_STATUS, SEVERITY, WordPill, deliveriesText } from './run-words'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../shared/error/use-error-line'
import { RUNS_PAGE, runHref } from './paths'

export { deliveriesText }

const PAGE = 200

// A date input's value ("2026-10-01") as the start of that day in the
// viewer's own time zone; `to` is the start of the day after, so the day
// itself is included.
const localDay = (v, plusDays = 0) => {
  if (!v) return ''
  const [y, m, d] = v.split('-').map(Number)
  return new Date(y, m - 1, d + plusDays).toISOString()
}

// runsPager pages GET /runs for the grid: the server pages by cursor
// (`next`), the grid by row index, so each block start remembers the cursor
// that reaches it. A short page is the last one even if the server has more
// (the next block would have no cursor at its start).
export const runsPager = (filters, setError) => {
  const cursors = { 0: '' }
  return async (start, end) => {
    const before = cursors[start]
    if (before === undefined) return { data: [], count: start }
    try {
      setError(null)
      const limit = Math.min(end - start, PAGE)
      const page = await listRuns({ ...filters, before, limit })
      const items = (page && page.items) || []
      if (page && page.next && items.length === limit) {
        cursors[start + items.length] = page.next
        return { data: items, count: -1 }
      }
      return { data: items, count: start + items.length }
    } catch (err) {
      setError(serverMessage(err, "Run history couldn't be loaded."), err)
      return { data: [], count: start }
    }
  }
}

// A compact filter of the toolbar row, named by its aria-label (CLAUDE.md
// "List page layout": no stacked labels); its first choice says "any".
// cap: a select whose options are free names (alerts, routes, targets) stops
// at a width of its own, so one long name never pushes the row onto two lines.
const Select = ({ id, label, any, cap = false, value, onChange, children }) =>
  <select id={id} aria-label={label} className={`form-select form-select-sm${cap ? ' ar-filter-cap' : ''}`} value={value} onChange={onChange}><option value="">{any}</option>{children}</select>

const BLANK = { category: '', trigger: '', status: '', routeID: '', targetID: '' }

// "Raised", the house time range (shared/date/time-range-filter.js:
// { range, from, to }), as the instants GET /runs takes: a preset reaches back
// from when it was picked with no end, so a newer alert is in the list when it
// reloads; a custom range is its whole local days; Any time is no bound.
export const ANY_TIME = { range: 'all', from: '', to: '' }
export const raisedWindow = (when, now = Date.now()) => {
  if (!when || when.range === 'all') return { from: '', to: '' }
  if (when.range === 'custom') return { from: localDay(when.from), to: localDay(when.to, 1) }
  const preset = RANGES.find(r => r.value === when.range && r.ms)
  return preset ? { from: new Date(now - preset.ms).toISOString(), to: '' } : { from: '', to: '' }
}

// Alert runs (/admin/alert-routing/runs; owner 2026-10-30: its own page in
// the sidebar): every alert raised, newest first, where it went and what
// happened. The house list page: the filters as one compact toolbar row that
// fits a 1280 px window (the house time range for when, the selects whose
// options are free names capped, ar-filter-cap), then the card. An alert opens
// in its drawer over the list.
export const RunsPage = () => {
  const catalog = useCatalog()
  const history = useHistory()
  const { search } = useLocation()
  const subjectRef = new URLSearchParams(search).get('subjectRef') || ''
  // A link may start the list at one target or route (a target's "View all
  // in run history"); the filters take it from there.
  const [f, setF] = useState(() => {
    const q = new URLSearchParams(search)
    return { ...BLANK, targetID: q.get('targetID') || '', routeID: q.get('routeID') || '' }
  })
  const [routes, setRoutes] = useState([])
  const [targets, setTargets] = useState([])
  const [error, failure, setError] = useErrorLine()
  const tableRef = useRef()
  useEffect(() => { listRoutes().then(v => setRoutes(v || []), () => {}); listTargets().then(v => setTargets(v || []), () => {}) }, [])
  const [when, setWhen] = useState(ANY_TIME)
  // the window is reckoned when the range is picked, not on every render
  const raised = useMemo(() => raisedWindow(when), [when])
  const filters = useMemo(() => ({ ...f, ...raised, subjectRef }), [f, raised, subjectRef])
  const getRows = useMemo(() => runsPager(filters, setError), [filters, setError])
  // A filter change is a new query, so it also clears a failure and retries.
  const set = k => e => { const v = e.target.value; setError(null); setF(x => ({ ...x, [k]: v })) }
  const triggers = f.category ? catalog.triggers.filter(t => t.category === f.category) : catalog.triggers
  return (
    <FleetPage title="Alert runs" purpose="Every alert raised, where it went and what happened.">
      {subjectRef && (
        <Notice tone="info">Showing the alerts about <code>{subjectRef}</code>. <Link to={RUNS_PAGE}>Show every alert</Link></Notice>
      )}
      <div className="sw-toolbar" role="group" aria-label="Filter the alerts">
        <Select id="ar-r-cat" label="Category" any="Every category" value={f.category} onChange={e => { const v = e.target.value; setError(null); setF(x => ({ ...x, category: v, trigger: '' })) }}>{catalog.categories.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}</Select>
        <Select id="ar-r-trig" label="Alert" any="Every alert" cap value={f.trigger} onChange={set('trigger')}>{triggers.map(t => <option key={t.kind} value={t.kind}>{t.label}</option>)}</Select>
        <Select id="ar-r-status" label="Status" any="Any status" value={f.status} onChange={set('status')}>{Object.entries(ALERT_STATUS).map(([k, w]) => <option key={k} value={k}>{w.label}</option>)}</Select>
        <Select id="ar-r-route" label="Route" any="Every route" cap value={f.routeID} onChange={set('routeID')}>{routes.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}</Select>
        <Select id="ar-r-target" label="Target" any="Every target" cap value={f.targetID} onChange={set('targetID')}>{targets.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}</Select>
        <TimeRangeFilter label="Raised" inline allTime value={when}
          onChange={next => { setError(null); setWhen(next.range === 'custom' ? { range: 'custom', from: next.from || '', to: next.to || '' } : { range: next.range, from: '', to: '' }) }} />
      </div>
      {/* A failure shows only its notice ("No alerts" under it would be
          untrue). The grid is unmounted, so Try again (or a filter change)
          mounts it afresh and it loads again. */}
      {error && (
        <Notice tone="bad" role="alert">{error} <button type="button" className="btn btn-sm sw-btn ms-1" onClick={() => setError(null)}>Try again</button><ErrorReference error={failure} /></Notice>
      )}
      {!error && <FleetGrid title="Alerts" subtitle="Newest first" tableRef={tableRef} rowHeight={ROW_HEIGHT_TWO_LINE}>
        <TableAgInfinite key={JSON.stringify(filters)} ref={tableRef} queryKey="" onGetRows={getRows} rowHeight={ROW_HEIGHT_TWO_LINE}
          onRowClicked={r => history.push(runHref(r.id))}
          emptyState={{ Icon: IconList, title: 'No alerts', message: 'Nothing matches these filters.' }}>
          <ColumnFormat field="raisedAt" header="Raised" sortable={false} minWidth={140}
            render={r => (r.data ? <span title={formatDateTime(r.value)}>{formatAgo(r.value)}</span> : '')} />
          <ColumnFormat field="title" header="Alert" sortable={false} minWidth={260} flex={2} stacked
            render={r => (r.data ? <span><span className="fw-semibold">{r.value}</span><span className="sw-cell-sub">{r.data.triggerLabel || r.data.trigger}</span></span> : '')} />
          <ColumnFormat field="severity" header="Severity" sortable={false} minWidth={130} render={r => (r.data ? <WordPill words={SEVERITY} value={r.value} /> : '')} />
          <ColumnFormat field="organizationName" header="Organization" sortable={false} minWidth={140} render={r => (r.data ? r.value || '' : '')} />
          <ColumnFormat field="matchedRoutes" header="Routes" sortable={false} minWidth={80} render={r => (r.data ? r.value : '')} />
          <ColumnFormat field="sent" header="Deliveries" sortable={false} minWidth={230} render={r => (r.data ? deliveriesText(r.data) : '')} />
          <ColumnFormat field="status" header="Status" sortable={false} minWidth={200}
            render={r => (r.data ? <span className="ar-pills ar-nowrap"><WordPill words={ALERT_STATUS} value={r.value} />{r.data.isTest && <Pill tone="info">Test</Pill>}</span> : '')} />
        </TableAgInfinite>
      </FleetGrid>}
    </FleetPage>
  )
}
